<script setup lang="ts">
import { computed } from "vue";
import type { Notification, NotificationSeverity } from "@/types";
import { useRouter } from "vue-router";

const props = defineProps<{
  notification: Notification;
}>();

const emit = defineEmits<{
  markAsRead: [id: string];
  delete: [id: string];
}>();

const router = useRouter();

const iconClass = computed(() => {
  switch (props.notification.severity) {
    case 0: // Info
      return "bi bi-info-circle text-primary";
    case 1: // Success
      return "bi bi-check-circle text-success";
    case 2: // Warning
      return "bi bi-exclamation-triangle text-warning";
    case 3: // Error
      return "bi bi-x-circle text-danger";
    default:
      return "bi bi-bell text-primary";
  }
});

const timeAgo = computed(() => {
  const now = new Date();
  const created = new Date(props.notification.createdAt);
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

  return created.toLocaleDateString();
});

const handleClick = () => {
  if (!props.notification.isRead) {
    emit("markAsRead", props.notification.id);
  }

  if (props.notification.actionUrl) {
    router.push(props.notification.actionUrl);
  }
};

const handleMarkAsRead = (event: Event) => {
  event.stopPropagation();
  emit("markAsRead", props.notification.id);
};

const handleDelete = (event: Event) => {
  event.stopPropagation();
  emit("delete", props.notification.id);
};
</script>

<template>
  <div
    class="notification-item px-2 px-sm-3 py-3 position-relative border-bottom"
    :class="{ unread: !notification.isRead, 'cursor-pointer': notification.actionUrl }"
    @click="handleClick"
  >
    <div class="d-flex align-items-start">
      <div class="notification-icon me-3">
        <i :class="iconClass" class="fs-4"></i>
      </div>
      <div class="flex-1 notification-content pe-5">
        <h3 class="fs-8 text-body-emphasis mb-1">
          {{ notification.title }}
        </h3>
        <p class="fs-9 text-body-highlight mb-2 fw-normal notification-message">
          {{ notification.message }}
        </p>
        <div v-if="notification.actionText && notification.actionUrl" class="mb-2">
          <button class="bb-btn-secondary-cta btn-sm">
            {{ notification.actionText }}
          </button>
        </div>
        <p class="text-body-secondary fs-10 mb-0">
          <span class="fw-bold">{{ timeAgo }}</span>
        </p>
      </div>
    </div>
    <!-- Dropdown positioned absolutely on the right -->
    <div class="dropdown notification-dropdown position-absolute top-0 end-0 mt-3 me-2">
      <button
        class="bb-btn-secondary-cta btn-sm dropdown-toggle dropdown-caret-none transition-none"
        title="Options"
        type="button"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        @click.stop
      >
        <i class="bi bi-three-dots fs-10 text-body"></i>
      </button>
      <div class="dropdown-menu dropdown-menu-start py-2">
        <a
          v-if="!notification.isRead"
          class="dropdown-item"
          href="#!"
          @click="handleMarkAsRead"
        >
          Mark as read
        </a>
        <a class="dropdown-item" href="#!" @click="handleDelete">Delete</a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.notification-item {
  transition: background-color 0.2s;

  &.cursor-pointer {
    cursor: pointer;
  }

  &:hover {
    background-color: var(--bs-tertiary-bg);
  }

  &.unread {
    position: relative;

    // Add a subtle blue indicator on the left for unread items
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 60%;
      background-color: var(--bs-primary);
      border-radius: 0 3px 3px 0;
    }
  }

  .notification-content {
    min-width: 0;
  }

  .notification-message {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}

.notification-item .notification-dropdown .dropdown-menu {
  position: absolute;
  top: 100%;
  right: auto;
  left: auto;
  transform: translate(-100%, 0);
  margin-top: 0.125rem;
}
</style>
